import React, { FC, Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ActionsDataGrid from './actions-data-grid/actions-data-grid.component';
import MaterialsDataGrid from './materials-data-grid/materials-data-grid.component';
import { ActionGridInput, MaterialGridInput } from 'models/order-input/shared';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { LabOrderContext } from 'pages/order-flow/lab-order/lab-order-context';
import { ActionMaterial, LabConstuction, OrderLabAction } from 'models/order-input/lab-order';
import { useLazyQuery } from '@apollo/client';
import { getCategorizedActionsTemplate } from 'apollo/templates/order';
import { actionPriceListForOrderTemplate, materialPriceListForOrderTemplate } from 'apollo/templates/client-directory';
import { MaterialPriceList } from 'models/material';
import { MaterialMacro } from 'models/material-macro';
import {
    convertCategorizedActionsToOrderLabActions,
    filterMaterialsByRemovedActions,
    createActionMaterialInput,
    toMaterialMacroOptions,
    createMacroMaterialInputs,
    syncLinkedMaterialQuantities,
    perToothQuantity,
} from './actions-and-materials-helper';
import { ActionSelectionPayload } from './actions-data-grid/action-selection';
import { CurrencyContext } from 'context/currency';

type ActionsAndMaterialsProps = {
    constructionIndex: number;
};
const ActionsAndMaterials: FC<ActionsAndMaterialsProps> = ({ constructionIndex }) => {
    const { t: translate } = useTranslation(['orders', 'actions-materials']);
    const { showBoundary } = useDenthubErrorBoundary();
    const { order, setOrder } = useContext(LabOrderContext);
    const { setCurrency } = useContext(CurrencyContext);
    const [actionPriceListForClient, setActionPriceListForClient] = useState<OrderLabAction[]>([]);
    const [materialPriceListForClient, setMaterialPriceListForClient] = useState<MaterialPriceList>(
        {} as MaterialPriceList,
    );
    // The customer controls the action list only when the clinic's ORDER_VERSION setting is
    // Action-based. order.isActionBasedOrder carries that decision - positive test,
    // parent-clinic inheritance applied, defaults to false - so a clinic whose settings have
    // not resolved falls back to the lab-priced grid instead of silently locking it.
    const disableForActionBased = Boolean(order.customer?._id && order.isActionBasedOrder);

    const currentConstruction = order.constructions[constructionIndex];

    const actionPriceListWithValidMacros = useMemo(() => {
        return actionPriceListForClient.map((action) => ({
            ...action,
            materialMacroOptions: toMaterialMacroOptions(action.materialMacros, materialPriceListForClient.materials),
        }));
    }, [actionPriceListForClient, materialPriceListForClient]);

    useEffect(() => {
        if (order.customer?._id) {
            getMaterialPriceList({ variables: { clientId: order.customer._id } });
            if (!disableForActionBased) {
                getActionPriceListForClient({ variables: { clientId: order.customer._id } });
            } else {
                getCategorizedActions({ variables: { clinicId: order.customer._id, orderId: order._id } });
            }
        }
    }, [order.customer?._id]);

    const [getMaterialPriceList] = useLazyQuery(materialPriceListForOrderTemplate, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        onCompleted(data) {
            setOrder((prev) => ({
                ...prev,
                materialCurrency: data.materialPriceListForClient?.currency ?? null,
            }));
            if (data.materialPriceListForClient?.materials) {
                // Action-based orders take their currency from the lab connection (see
                // getCategorizedActions below), so the material price list must not set it.
                if (!disableForActionBased) {
                    if (!order.currency || order.currency === 'XXX') {
                        setCurrency(data.materialPriceListForClient.currency);
                    } else {
                        setCurrency(order.currency);
                    }
                }
                setMaterialPriceListForClient(data.materialPriceListForClient);
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [getActionPriceListForClient] = useLazyQuery(actionPriceListForOrderTemplate, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        onCompleted(data) {
            setOrder((prev) => ({
                ...prev,
                actionCurrency: data.actionPriceListForClient?.currency ?? null,
            }));
            if (data.actionPriceListForClient?.actions) {
                if (!order.currency || order.currency === 'XXX') {
                    setCurrency(data.actionPriceListForClient.currency);
                } else {
                    setCurrency(order.currency);
                }
                setActionPriceListForClient(data.actionPriceListForClient.actions);
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [getCategorizedActions] = useLazyQuery(getCategorizedActionsTemplate, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        onError: (err) => {
            console.error(err, 'err');
            showBoundary(err);
        },
        onCompleted: ({ categorizedActions }) => {
            // Every row carries the same currency: the one set on the clinic's connection to
            // this lab, falling back to the action set's default. null when neither is set,
            // which the constructions step surfaces and blocks on.
            const actionCurrency = categorizedActions?.[0]?.currency ?? null;
            setOrder((prev) => ({ ...prev, actionCurrency }));
            if (order.currency && order.currency !== 'XXX') {
                setCurrency(order.currency);
            } else if (actionCurrency) {
                setCurrency(actionCurrency);
            }
            const actions = convertCategorizedActionsToOrderLabActions(categorizedActions);
            setActionPriceListForClient(actions);
        },
    });

    const handleMaterialsChange = (materials: MaterialGridInput[], isGridValid: boolean) => {
        setOrder((prev) => {
            const updatedConstructions = prev.constructions.map((construction, idx) =>
                idx === constructionIndex
                    ? { ...construction, materials, isMaterialGridValid: isGridValid }
                    : construction,
            );
            return { ...prev, constructions: updatedConstructions };
        });
    };

    const handleActionsChange = (actions: ActionGridInput[], isGridValid: boolean) => {
        setOrder((prev) => {
            const prevActions = prev.constructions[constructionIndex]?.actions || [];
            const removedActionIds = prevActions
                .filter((prevAction) => !actions.some((action) => action._id === prevAction._id))
                .map((action) => action.actionId);

            const updatedConstructions = prev.constructions.map((construction, idx) => {
                if (idx !== constructionIndex) return construction;
                const filteredMaterials = filterMaterialsByRemovedActions(construction.materials, removedActionIds);
                // Both material pickers run on action selection, before the quantity column
                // is filled in, so follow the action's quantity when it changes afterward.
                const syncedMaterials = syncLinkedMaterialQuantities(filteredMaterials, prevActions, actions);
                return {
                    ...construction,
                    actions,
                    isActionGridValid: isGridValid,
                    materials: syncedMaterials,
                    // Removing an action drops its materials, which can clear the only
                    // invalid row - without this the flag would stay false and leave Next
                    // disabled with nothing on screen left to fix.
                    isMaterialGridValid: syncedMaterials.every((material) => material.isValid),
                };
            });

            return { ...prev, constructions: updatedConstructions };
        });
    };

    // The step gates on the construction's isMaterialGridValid, and the materials grid only
    // recomputes that when the user edits a row - so a programmatic append has to carry the
    // verdict itself, or a row that lands invalid (macro material missing its batch) would
    // never block Next.
    const appendMaterialsToConstruction = (
        construction: LabConstuction,
        materialInputs: MaterialGridInput[],
    ): LabConstuction => {
        const materials = [...(construction.materials || []), ...materialInputs];
        return {
            ...construction,
            materials,
            isMaterialGridValid: materials.every((material) => material.isValid),
        };
    };

    const handleActionMaterialAdded = (material: ActionMaterial, selection: ActionSelectionPayload) => {
        setOrder((prev) => {
            const materialInput = createActionMaterialInput(material, selection.quantity);
            const updatedConstructions = prev.constructions.map((construction, idx) =>
                idx === constructionIndex ? appendMaterialsToConstruction(construction, [materialInput]) : construction,
            );
            return { ...prev, constructions: updatedConstructions };
        });
    };

    const handleMaterialMacroAdded = (materialMacro: MaterialMacro, selection: ActionSelectionPayload) => {
        setOrder((prev) => {
            const materialInputs = createMacroMaterialInputs(
                materialMacro,
                materialPriceListForClient.materials || [],
                selection.quantity,
                selection.actionId,
            );
            const updatedConstructions = prev.constructions.map((construction, idx) =>
                idx === constructionIndex ? appendMaterialsToConstruction(construction, materialInputs) : construction,
            );
            return { ...prev, constructions: updatedConstructions };
        });
    };

    return (
        <Fragment>
            <Box mb={4}>
                <Box mb={2}>
                    <Typography variant="h3" gutterBottom>
                        {translate('orders:ACTIONS')}
                    </Typography>
                </Box>
                {disableForActionBased && (
                    <Box mb={1}>
                        <Alert severity="info">{translate('actions-materials:CUSTOMER_CONTROLLS_ACTIONS_INFO')}</Alert>
                    </Box>
                )}
                <Box>
                    <ActionsDataGrid
                        priceList={actionPriceListWithValidMacros}
                        actions={currentConstruction?.actions || []}
                        onChange={handleActionsChange}
                        warranty={currentConstruction?.warranty}
                        isTioTandvard={currentConstruction?.isTioTandvard}
                        isActionBasedOrder={order.isActionBasedOrder}
                        disableForActionBased={disableForActionBased}
                        perToothQuantity={perToothQuantity(currentConstruction?.teethNumbers)}
                        onActionMaterialAdded={handleActionMaterialAdded}
                        onMaterialMacroAdded={handleMaterialMacroAdded}
                    />
                </Box>
            </Box>
            <Box mb={4}>
                <Box mb={2}>
                    <Typography variant="h3" gutterBottom>
                        {translate('orders:MATERIALS')}
                    </Typography>
                </Box>
                <Box>
                    <MaterialsDataGrid
                        priceList={materialPriceListForClient}
                        materials={currentConstruction?.materials || []}
                        onChange={handleMaterialsChange}
                        warranty={currentConstruction?.warranty}
                        allowMaterialMacros
                    />
                </Box>
            </Box>
        </Fragment>
    );
};

export default ActionsAndMaterials;
